import { useEffect, useRef, useState } from 'react';
import type { Notification } from '@witmetrics/utils';
import {
  useAllNotifications,
  useCurrentUser,
  useDispatch,
  useUnreadNotificationsCount,
} from '@witmetrics/data-store';
import { useToggle } from '@/hooks/useToggle';
import { useAppState } from '@/providers/AppStateProvider';
import IconButton from '@/components/IconButton';
import { NotificationIcon } from '@/icons';
import Tooltip from '@/components/Tooltip';
import Badge from '@/components/Badge';
import NotificationsMenu from '@/menus/NotificationsMenu';
import NotificationToaster from './NotificationToaster';
import {
  classes,
  fetchNotifications,
  sortNotifications,
  styles,
} from './utils';

export type NotificationsProps = {
  className?: string;
};

export default function Notifications({ className }: NotificationsProps) {
  const unreadRef = useRef<number | null>(null);
  const menuToggleRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const { onApiError } = useAppState();
  const currentUser = useCurrentUser();
  const notifications = useAllNotifications();
  const unreadCount = useUnreadNotificationsCount();
  const [activeNotification, setActiveNotification] =
    useState<Notification | null>(null);
  const [isFetchingData, toggleFetchingData] = useToggle();
  const [isMenuOpen, toggleMenu] = useToggle();

  useEffect(() => {
    if (currentUser) fetchData();
  }, [currentUser]);

  useEffect(() => {
    if (unreadCount !== null) checkNewNotifications();
  }, [unreadCount]);

  const fetchData = async () => {
    try {
      toggleFetchingData(true);
      const data = await fetchNotifications();
      dispatch.addNotifications(data.notifications);
      dispatch.setUnreadNotificationsCount(data.unreadCount);
      toggleFetchingData(false);
    } catch (err) {
      toggleFetchingData(false);
      onApiError(err, 'Error fetching notifications', () => fetchData());
    }
  };

  const checkNewNotifications = () => {
    // If new unread count is greater than the current one, show a toaster for the most recent notification
    if (unreadRef.current === null) unreadRef.current = unreadCount!;
    if (unreadCount! > unreadRef.current) {
      const notification = sortNotifications(notifications)[0];
      if (notification) setActiveNotification(notification);
    }
    if (unreadCount !== unreadRef.current) unreadRef.current = unreadCount;
  };

  return (
    <>
      <div ref={menuToggleRef}>
        <Tooltip arrow enterDelay={0} placement="bottom" title="Notifications">
          <IconButton className={className} onClick={() => toggleMenu()}>
            <Badge badgeContent={unreadCount} color="orange-3">
              <NotificationIcon
                sx={unreadCount && unreadCount > 0 ? styles.unread : {}}
                className={classes.icon}
              />
            </Badge>
          </IconButton>
        </Tooltip>
      </div>
      {isMenuOpen && (
        <NotificationsMenu
          anchorTo={menuToggleRef.current}
          open
          isFetchingData={isFetchingData}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          notifications={sortNotifications(notifications)}
          onClose={() => toggleMenu()}
        />
      )}
      {activeNotification && (
        <NotificationToaster
          notification={activeNotification}
          onClose={() => setActiveNotification(null)}
        />
      )}
    </>
  );
}
